import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { maintenanceApi, companiesApi, usersApi } from "../services/api";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { useNotification } from "../context/NotificationContext";
import { isDueDatePast } from "../utils/dueDate";

const CATEGORY_BADGE = {
  network:   "bg-blue-100 text-blue-700",
  hardware:  "bg-gray-100 text-gray-700",
  software:  "bg-purple-100 text-purple-700",
  security:  "bg-red-100 text-red-700",
  backup:    "bg-yellow-100 text-yellow-700",
  other:     "bg-gray-100 text-gray-500",
};
const CATEGORY_HE = {
  network:  "רשת",
  hardware: "חומרה",
  software: "תוכנה",
  security: "אבטחה",
  backup:   "גיבוי",
  other:    "אחר",
};
// Categories chosen in TicketDetail's "move to maintenance" dialog are stored on the new item too.
const QUEUE_CAT_HE = { backups: "גיבויים", m365: "M365", av_edr: "AV/EDR", updates: "עדכונים ותיקונים", firewall: "חומת אש", email_security: "אבטחת מייל", reports: "דוחות" };
// unassigned: update_maintenance auto-reverts status here when a technician is cleared from an
// in_progress item (maintenance.py) -- omitted here left the status <select> with no matching
// <option> and the badge unstyled, same class of drift already fixed for ticket statuses. waiting
// and closed are real, reachable values too (maintenance.py's own MAINT_DONE/"waiting" stat
// checks, mobile/src/utils/labels.ts's MAINTENANCE_STATUS_LABELS already covers all seven) --
// missing them here left the same <select>-falls-back-to-the-first-option bug for an item that
// was actually "waiting" or "closed": it silently displayed and behaved as "unassigned", and
// touching the dropdown at all could overwrite the real status with whatever it fell back to.
const STATUS_HE  = { unassigned: "לא הוקצה", open: "פתוח", in_progress: "בטיפול", waiting: "ממתין", done: "הושלם", closed: "סגור", cancelled: "בוטל" };
const MAINT_FINISHED = new Set(["done", "cancelled", "closed"]);   // the backend's MAINT_DONE: a cancelled item is never overdue
const PRIORITY_HE = { low: "נמוכה", medium: "בינונית", high: "גבוהה", critical: "קריטית" };
const STATUS_BADGE = {
  unassigned: "bg-gray-100 text-gray-600",
  open:       "bg-blue-100 text-blue-700",
  in_progress:"bg-yellow-100 text-yellow-700",
  waiting:    "bg-purple-100 text-purple-700",
  done:       "bg-green-100 text-green-700",
  closed:     "bg-gray-100 text-gray-500",
  cancelled:  "bg-gray-100 text-gray-400",
};
const PRIORITY_BADGE = {
  low:      "bg-gray-100 text-gray-600",
  medium:   "bg-yellow-100 text-yellow-700",
  high:     "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const asUtc = (iso) => /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(asUtc(iso));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const isPast = isDueDatePast;

export default function MaintenanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { addNotification } = useNotification();

  const { data: item, isLoading, isError: itemFailed, error: itemError, refetch: refetchItem } = useQuery({
    queryKey: ["maintenance", id],
    queryFn: () => maintenanceApi.get(id),
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });
  const { data: users = [] }     = useQuery({ queryKey: ["users"],     queryFn: () => usersApi.list() });

  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editDesc, setEditDesc] = useState(false);
  const [descDraft, setDescDraft] = useState("");
  const [editNotes, setEditNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  // null = not being edited (show the saved value). The field used to PUT on every keystroke while showing
  // the server value, so fast typing lost characters (two keystrokes sent values built from the same stale text).
  const [recurrenceDraft, setRecurrenceDraft] = useState(null);
  // /maintenance/:id reuses this component when only the id changes (Back/Forward), so a draft typed on one
  // item must not be shown on, or saved to, the next one.
  useEffect(() => { setRecurrenceDraft(null); }, [id]);

  // ["maintenance", id] does not prefix-match MaintenancePage's ["maintenance", assignedToFilter] or
  // Dashboard's ["maintenance-dashboard"]/["maintenance-dashboard-stats"] -- invalidate those too, since
  // this mutation can change title/description/recurrence/status, all shown on those list views.
  // ["maintenance", id] does not prefix-match MaintenancePage's ["maintenance", assignedToFilter] or
  // Dashboard's ["maintenance-dashboard"]/["maintenance-dashboard-stats"] -- invalidate those too, since
  // this mutation can change title/description/recurrence/status, all shown on those list views.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["maintenance", id] });
    qc.invalidateQueries({ queryKey: ["maintenance"] });
    qc.invalidateQueries({ queryKey: ["maintenance-dashboard"] });
    qc.invalidateQueries({ queryKey: ["maintenance-dashboard-stats"] });
  };

  const update = useMutation({
    mutationFn: (data) => maintenanceApi.update(id, data),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון משימת תחזוקה", "error"),
  });

  const saveTitle = () => {
    if (titleDraft.trim() && titleDraft !== item.title) update.mutate({ title: titleDraft.trim() });
    setEditTitle(false);
  };

  const saveRecurrence = () => {
    if (recurrenceDraft === null) return;
    const next = recurrenceDraft.trim();
    if (next !== (item.recurrence_pattern || "")) update.mutate({ recurrence_pattern: next || null });
    setRecurrenceDraft(null);
  };

  const saveDesc = () => {
    if (descDraft !== (item.description || "")) update.mutate({ description: descDraft });
    setEditDesc(false);
  };

  const saveNotes = () => {
    if (notesDraft !== (item.notes || "")) update.mutate({ notes: notesDraft });
    setEditNotes(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20" dir="rtl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }
  if (!item && itemFailed && itemError?.response?.status !== 404) {   // `!item`: a failed background refetch keeps the loaded page
    return (
      <div role="alert" className="text-center py-20 text-red-600" dir="rtl">
        <p className="font-medium">לא ניתן לטעון את הפריט</p>
        <button onClick={() => refetchItem()} className="mt-2 text-sm text-brand-600 hover:underline">נסה שוב</button>
      </div>
    );
  }
  if (!item) return <p className="text-center py-20 text-gray-400" dir="rtl">פריט לא נמצא</p>;

  return (
    <div className="space-y-5 max-w-3xl" dir="rtl">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <span>→</span> חזרה
      </button>

      {/* Source ticket banner */}
      {item.source_ticket && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm">
          <span className="text-blue-700 font-medium">פריט זה נוצר אוטומטית מקריאה:</span>
          <Link
            to={`/tickets/${item.source_ticket.id}`}
            className="text-blue-600 font-semibold hover:underline"
          >
            #{item.source_ticket.ticket_number} — {item.source_ticket.title}
            {ticketHasAttachments(item.source_ticket) && (
              <span title={`${attachmentCount(item.source_ticket)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-500 mr-2">
                📎
                <span className="text-[10px] font-semibold">{attachmentCount(item.source_ticket)}</span>
              </span>
            )}
          </Link>
        </div>
      )}

      {/* Main card */}
      <div className="bg-white rounded-xl border p-6 space-y-5">
        {/* Title + category badge */}
        <div className="flex items-start gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap mt-1 ${CATEGORY_BADGE[item.category] || "bg-gray-100 text-gray-500"}`}>
            {CATEGORY_HE[item.category] || QUEUE_CAT_HE[item.category] || item.category || "—"}
          </span>
          <div className="flex-1">
            {editTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditTitle(false); }}
                className="text-xl font-bold w-full border-b-2 border-green-400 outline-none bg-transparent pb-1"
              />
            ) : (
              <h1
                className="text-xl font-bold text-gray-900 cursor-pointer hover:text-green-700 transition-colors"
                onClick={() => { setTitleDraft(item.title); setEditTitle(true); }}
                title="לחץ לעריכה"
              >
                {item.title}
              </h1>
            )}
          </div>
        </div>

        {/* Fields grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
            <select
              value={item.status}
              onChange={(e) => update.mutate({ status: e.target.value })}
              className={`border rounded-lg px-2 py-1.5 text-sm w-full font-medium ${STATUS_BADGE[item.status] || ""}`}
            >
              {Object.entries(STATUS_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
            <select
              value={item.priority}
              onChange={(e) => update.mutate({ priority: e.target.value })}
              className={`border rounded-lg px-2 py-1.5 text-sm w-full font-medium ${PRIORITY_BADGE[item.priority] || ""}`}
            >
              {Object.entries(PRIORITY_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">קטגוריה</label>
            <select
              value={item.category || ""}
              onChange={(e) => update.mutate({ category: e.target.value })}
              className="border rounded-lg px-2 py-1.5 text-sm w-full text-gray-700"
            >
              <option value="" disabled>—</option>
              {Object.entries({ ...CATEGORY_HE, ...(item.category in QUEUE_CAT_HE ? { [item.category]: QUEUE_CAT_HE[item.category] } : {}) }).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">חברה</label>
            <select
              value={item.company_id ?? ""}
              onChange={(e) => update.mutate({ company_id: e.target.value || null })}
              className="border rounded-lg px-2 py-1.5 text-sm w-full text-gray-700"
            >
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">אחראי</label>
            <select
              value={item.assigned_to ?? ""}
              onChange={(e) => update.mutate({ assigned_to: e.target.value || null })}
              className="border rounded-lg px-2 py-1.5 text-sm w-full text-gray-700"
            >
              <option value="">—</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">תאריך יעד</label>
            <div className={`flex items-center gap-2 ${isPast(item.due_date) && !MAINT_FINISHED.has(item.status) ? "text-red-600" : "text-gray-700"}`}>
              <input
                type="date"
                value={item.due_date ? item.due_date.slice(0, 10) : ""}
                onChange={(e) => update.mutate({ due_date: e.target.value || null })}
                className={`border rounded-lg px-2 py-1.5 text-sm w-full ${isPast(item.due_date) && !MAINT_FINISHED.has(item.status) ? "border-red-300 text-red-600" : "text-gray-700"}`}
              />
              {isPast(item.due_date) && !MAINT_FINISHED.has(item.status) && (
                <span className="text-xs font-semibold text-red-600 whitespace-nowrap">באיחור!</span>
              )}
            </div>
          </div>
        </div>

        {/* Recurring */}
        <div className="flex items-center gap-3 py-2 border-t border-gray-100">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => update.mutate({ recurring: !item.recurring })}
              className={`relative w-10 h-5 rounded-full transition-colors ${item.recurring ? "bg-green-500" : "bg-gray-200"}`}
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${item.recurring ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <span className="text-sm text-gray-700">תחזוקה חוזרת</span>
          </label>
          {item.recurring && (
            <input
              value={recurrenceDraft ?? (item.recurrence_pattern || "")}
              maxLength={50}
              onChange={(e) => setRecurrenceDraft(e.target.value)}
              onBlur={saveRecurrence}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="תבנית חזרה (למשל: שבועי, חודשי)"
              className="flex-1 border rounded-lg px-2 py-1 text-sm text-gray-700"
            />
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">תיאור</label>
          {editDesc ? (
            <textarea
              autoFocus
              rows={3}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveDesc}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            />
          ) : (
            <p
              onClick={() => { setDescDraft(item.description || ""); setEditDesc(true); }}
              className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded p-2 min-h-[2.5rem] border border-transparent hover:border-gray-200"
            >
              {item.description || <span className="text-gray-300 italic">לחץ להוספת תיאור…</span>}
            </p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">הערות</label>
          {editNotes ? (
            <textarea
              autoFocus
              rows={3}
              value={notesDraft}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={saveNotes}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            />
          ) : (
            <p
              onClick={() => { setNotesDraft(item.notes || ""); setEditNotes(true); }}
              className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded p-2 min-h-[2.5rem] border border-transparent hover:border-gray-200"
            >
              {item.notes || <span className="text-gray-300 italic">לחץ להוספת הערות…</span>}
            </p>
          )}
        </div>

        {/* Meta */}
        <div className="text-xs text-gray-400 pt-2 border-t border-gray-100">
          נוצר: {fmtDate(item.created_at)}
          {item.updated_at && item.updated_at !== item.created_at && ` · עודכן: ${fmtDate(item.updated_at)}`}
        </div>
      </div>
    </div>
  );
}
